/**
 * A room's Audience tab: its people in groups, each with a send, and the picked group's people in a list that
 * keeps loading. A weekly room shows its last live session or all time; any other room shows all time.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { RoomAudienceResponse, RoomGroup, RoomGroupKey, RoomPerson, WebinarDetail } from "../../../shared/types";
import { Button } from "../../components/ui/Button";
import { FaceStack, Ring } from "../../components/ui/Face";
import { Icon } from "../../components/ui/Icon";
import { Seg } from "../../components/ui/Seg";
import { useToast } from "../../components/ui/Toast";
import { ApiError, api } from "../../lib/api";
import type { HostCircleRow } from "../../../shared/talk";
import { type CircleSeed, useOpenDirect, useStartCircle } from "../../talk/handoff";
import { AfterSecondLive } from "../../talk/parts";
import { circlePath } from "../../talk/places";
import { allTimeHead, longDay, teamTestsLine } from "./liveWords";
import { PrivateSendSheet } from "./PrivateSendSheet";
import { circleMadeFrom } from "./RoomTalk";
import { groupLine, leftOutLine, sentLine } from "./roomWords";
import { type AudienceScope, useAudience } from "./useAudience";

const n = (v: number) => v.toLocaleString("en-US");
const facesOf = (g: RoomGroup) => g.faces.map((f) => ({ name: f.name, photo: f.avatar_url }));
/** A group opens with its first few people and "and 48 more · Show all", as the design draws it. */
const FIRST_ROWS = 4;

/** The sent line a group shows once a note has gone to it, from the server's own record. */
function SentLine({ sent }: { sent: { at: number; count: number } }) {
  return (
    <div className="sent">
      <Icon name="check" />
      {sentLine(sent, Date.now())}
    </div>
  );
}

/** One person in a group: their ring, how long they stayed, the one thing worth knowing, and a note. */
function PersonRow({ p, onNote }: { p: RoomPerson; onNote: (p: RoomPerson) => void }) {
  const navigate = useNavigate();
  const open = () => navigate(`/people/${p.registrantId}`);
  const about = [p.tags.join(", "), p.source].filter(Boolean).join(" · ");
  return (
    <div
      className="prow"
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target === e.currentTarget) open();
      }}
    >
      <div className="who">
        <Ring name={p.name} photo={p.avatar_url} size={38} percent={p.pct} live={p.watching} />
        <div>
          <div className="nm"><bdi>{p.name}</bdi></div>
          {about ? <div className="ds">{about}</div> : null}
        </div>
      </div>
      <div className="pstay">
        <div className="b6 small">{p.stay}</div>
        <div className={p.pct >= 100 ? "bar mt6 goodb" : "bar mt6"} style={{ maxWidth: 140 }}>
          <i style={{ width: `${p.pct}%` }} />
        </div>
      </div>
      <div className="small ink2 pnote">{p.note}</div>
      <Button
        size="s"
        icon="send"
        onClick={(e) => {
          e.stopPropagation();
          onNote(p);
        }}
      >
        Note
      </Button>
    </div>
  );
}

/** The picked group's people, a page at a time; the next page loads as the list's end comes into view. */
function GroupList({
  roomId,
  group,
  label,
  session,
  line,
  seed,
}: {
  roomId: string;
  group: RoomGroup;
  label: string;
  session: string | undefined;
  line: string;
  seed: CircleSeed;
}) {
  const navigate = useNavigate();
  const startCircle = useStartCircle();
  const toast = useToast();
  const openDirect = useOpenDirect();
  const [people, setPeople] = useState<RoomPerson[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [all, setAll] = useState(false);
  const end = useRef<HTMLDivElement>(null);

  const page = useCallback(
    (cursor: string | undefined, fresh: boolean) => {
      setBusy(true);
      setFailed(false);
      api.webinars
        .audienceGroup(roomId, group.key, session, cursor)
        .then((r) => {
          setPeople((was) => (fresh ? r.people : [...was, ...r.people]));
          setNext(r.next);
        })
        .catch(() => setFailed(true))
        .finally(() => setBusy(false));
    },
    [roomId, group.key, session],
  );

  useEffect(() => {
    setPeople([]);
    setNext(null);
    page(undefined, true);
  }, [page]);

  useEffect(() => {
    const el = end.current;
    if (!all || !el || !next || busy || typeof IntersectionObserver === "undefined") return;
    const seen = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) page(next, false);
    });
    seen.observe(el);
    return () => seen.disconnect();
  }, [all, next, busy, page]);

  const shown = all ? people : people.slice(0, FIRST_ROWS);
  const rest = people.slice(FIRST_ROWS, FIRST_ROWS + 3).map((p) => ({ name: p.name, photo: p.avatar_url }));

  // A note to one person goes into their own thread, which reaches their page.
  const note = (p: RoomPerson) => {
    openDirect(p.registrantId).catch((err: unknown) => toast(err instanceof ApiError ? err.message : "We couldn't open their conversation"));
  };

  return (
    <section className="card plist mt20" id="glist">
      <div className="card-h" style={{ paddingBottom: 12 }}>
        <div>
          <h2>
            {label} · {n(group.count)}
          </h2>
          {line ? <div className="sub">{line}</div> : null}
        </div>
        <div className="rp-acts">
          <Button size="s" icon="target" onClick={() => startCircle(seed)}>
            Start a circle
          </Button>
          <button type="button" className="link" onClick={() => navigate(`/people?webinar=${encodeURIComponent(roomId)}`)}>
            Open in People <Icon name="chev" />
          </button>
        </div>
      </div>
      {shown.map((p) => (
        <PersonRow key={p.registrantId} p={p} onNote={note} />
      ))}
      {group.count === 0 ? <div className="prow-empty small muted">No one in this group yet.</div> : null}
      {!all && people.length > 0 && group.count > shown.length && !failed ? (
        <div className="prow prest">
          <div className="who">
            {rest.length ? <FaceStack people={rest} size={30} /> : null}
            <div className="ds">and {n(group.count - shown.length)} more</div>
          </div>
          <div />
          <div />
          <Button size="s" variant="q" onClick={() => setAll(true)}>
            Show all
          </Button>
        </div>
      ) : null}
      {failed && !all ? (
        <div className="prow-empty">
          <span className="small muted">We couldn't load who is in it.</span>
          <Button size="s" variant="q" onClick={() => page(undefined, true)}>
            Try again
          </Button>
        </div>
      ) : null}
      {!all ? null : failed ? (
        <div className="prow-empty">
          <span className="small muted">We couldn't load the rest.</span>
          <Button size="s" variant="q" onClick={() => page(next ?? undefined, people.length === 0)}>
            Try again
          </Button>
        </div>
      ) : next ? (
        <div className="prow-empty" ref={end}>
          <span className="small muted">
            and {n(group.count - people.length)} more
          </span>
          <Button size="s" variant="q" disabled={busy} onClick={() => page(next, false)}>
            {busy ? "Loading…" : "Show more"}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

export function AudienceTab({ detail, hostFirst, circles }: { detail: WebinarDetail; hostFirst: string; circles?: HostCircleRow[] }) {
  const navigate = useNavigate();
  const { chosen, scope, setScope, data, failed, retry, picked, setPicked, sending, closeSend, startSend, replayLine, wordsOf, segLabel, wideLabel } =
    useAudience(detail, hostFirst);
  const session = data?.scope === "session" ? data.session : null;

  const seg = chosen ? (
    <div className="row g12 wrap" style={{ marginBottom: 16 }}>
      <Seg<AudienceScope>
        label="Which people"
        value={scope}
        onChange={setScope}
        options={[
          { value: "session", label: segLabel },
          { value: "all", label: wideLabel },
        ]}
      />
      {scope === "session" && data && session ? <SessionLine data={data} /> : null}
    </div>
  ) : null;

  if (failed && !data) {
    return (
      <>
        {seg}
        <div className="note">
          <Icon name="info" />
          <span className="sp">We couldn't load who came.</span>
          <Button size="s" onClick={retry}>
            Try again
          </Button>
        </div>
      </>
    );
  }
  if (!data) {
    return (
      <>
        {seg}
        <div className="groups" aria-busy="true">
          {["a", "b", "c"].map((k) => (
            <div key={k} className="grp sk" />
          ))}
        </div>
      </>
    );
  }

  const empty = data.scope === "series" && data.groups.every((g) => g.count === 0);
  const shown = empty ? [] : data.groups;
  const group = empty ? undefined : (data.groups.find((g) => g.key === picked) ?? data.groups[0]);
  const isSession = data.scope === "session";
  const tagOf = (key: RoomGroupKey) => {
    const made = circleMadeFrom(circles, data.scope, key, session?.id);
    return made ? (
      <button
        type="button"
        className="rp-circ"
        onClick={(e) => {
          e.stopPropagation();
          navigate(circlePath(made.circleId));
        }}
      >
        <Icon name="target" />
        {made.name}
      </button>
    ) : null;
  };
  return (
    <>
      {seg}
      {!isSession ? <AllTimeHead detail={detail} data={data} /> : null}
      {empty ? <AfterSecondLive /> : null}
      <div className={isSession ? "groups" : "groups mt20"}>
        {shown.map((g) => {
          const words = wordsOf(g.key);
          const act = g.sent ? words.again(g.reach) : words.act(g.reach);
          const was = g.sent;
          const line = groupLine(g, data.groups, detail, data.scope);
          return (
            <div
              key={g.key}
              className={group?.key === g.key ? "grp on" : "grp"}
              role="button"
              tabIndex={0}
              aria-pressed={group?.key === g.key}
              onClick={() => setPicked(g.key)}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
                  e.preventDefault();
                  setPicked(g.key);
                }
              }}
            >
              <div className="row">
                <span className="gn">{n(g.count)}</span>
                <span className="sp" />
                {g.faces.length ? <FaceStack people={facesOf(g).slice(0, isSession ? 5 : 4)} size={26} /> : null}
              </div>
              <div className="gl">{words.label}</div>
              {tagOf(g.key)}
              {line ? <div className="gd">{line}</div> : null}
              {!isSession && g.key !== "watching" ? (
                <button
                  type="button"
                  className="link"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPicked(g.key);
                    document.getElementById("glist")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  See who <Icon name="chev" />
                </button>
              ) : null}
              {was ? <SentLine sent={was} /> : null}
              {act && g.reach > 0 ? (
                <Button
                  size="s"
                  variant={g.key === "missed" ? "p" : "soft"}
                  icon="send"
                  onClick={(e) => {
                    e.stopPropagation();
                    startSend(g);
                  }}
                >
                  {act}
                </Button>
              ) : null}
              {!was && replayLine(g) ? (
                <div className="auto">
                  <Icon name="zap" />
                  <span>{replayLine(g)}</span>
                </div>
              ) : null}
              {!was && leftOutLine(g.unreachable) ? <div className="gd gout">{leftOutLine(g.unreachable)}</div> : null}
            </div>
          );
        })}
      </div>
      {group ? (
        <GroupList
          key={`${data.scope}:${group.key}`}
          roomId={detail.id}
          group={group}
          label={wordsOf(group.key).label}
          session={session?.id ?? (data.scope === "session" ? undefined : data.scope)}
          line={groupLine(group, data.groups, detail, data.scope)}
          seed={{ webinarId: detail.id, scope: data.scope, key: group.key, ...(session ? { session: session.id } : {}) }}
        />
      ) : null}
      {sending ? (
        <PrivateSendSheet
          key={sending.seed.key}
          send={sending}
          onClose={closeSend}
          onSent={retry}
        />
      ) : null}
    </>
  );
}

/** "116 registered for Tuesday. 52 came, 31 stayed to the end and 4 became customers." */
function SessionLine({ data }: { data: RoomAudienceResponse }) {
  const count = (k: RoomGroupKey) => data.groups.find((g) => g.key === k)?.count ?? 0;
  const s = data.session;
  if (!s) return null;
  const day = longDay(s.startsAt, s.timezone).split(" ")[0];
  return (
    <span className="small muted">
      <b className="ink2">
        {n(s.registered)} registered for {day}.
      </b>{" "}
      {n(count("came"))} came, {n(count("end"))} stayed to the end and {n(count("customers"))} became{" "}
      {count("customers") === 1 ? "a customer" : "customers"}.
    </span>
  );
}

/** All time: the room's chain from registered to became customers. */
function AllTimeHead({ detail, data }: { detail: WebinarDetail; data: RoomAudienceResponse }) {
  const c = data.chain;
  const { title, sub } = allTimeHead(detail);
  const came = c.registered ? `${Math.round((c.came / c.registered) * 100)}%` : "";
  const stats: Array<[string, string, string]> = [
    ["Registered", n(c.registered), ""],
    ["Came", n(c.came), came ? `${came} of registered` : ""],
    ["Reached the offer", n(c.reached), ""],
    ["Clicked the offer", n(c.clicked), ""],
    ["Became customers", n(c.customers), ""],
  ];
  return (
    <section className="card">
      <div className="card-h">
        <div>
          <h2>{title}</h2>
          {sub ? <div className="sub">{sub}</div> : null}
          {detail.team_tests > 0 ? <div className="sub">{teamTestsLine(detail.team_tests)}</div> : null}
        </div>
      </div>
      <div className="card-b">
        <div className="stat4 stat5">
          {stats.map(([label, value, em]) => (
            <div key={label} className="stat">
              <b>{value}</b>
              <span>{label}</span>
              {em ? <em>{em}</em> : null}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
